import { ref, watch, type Ref } from 'vue';
import { format } from 'date-fns';

/**
 * Generic composable for persisting filter values in localStorage for the current date only.
 * Automatically cleans up old entries from previous dates.
 * 
 * @example
 * ```typescript
 * // Basic usage with permanent and daily filters
 * const filterPersistence = useFilterPersistence({ 
 *   pageId: 'myPage',
 *   permanentFilters: ['selectedView', 'userPreferences'] // These persist across days
 * });
 * 
 * // Define your filters
 * const searchTerm = ref(''); // Daily filter - resets each day
 * const dateRange = ref([new Date(), new Date()]); // Daily filter
 * const selectedView = ref('grid'); // Permanent filter - persists across days
 * const userPreferences = ref({ theme: 'dark' }); // Permanent filter
 * 
 * // Restore saved filters on page load
 * onMounted(() => {
 *   const filterRefs = { searchTerm, dateRange, selectedView, userPreferences };
 *   const restored = filterPersistence.restoreFilters(filterRefs);
 *   
 *   // Setup auto-save watchers
 *   filterPersistence.createFilterWatchers(filterRefs);
 * });
 * ```
 */

export type FilterValue = string | number | boolean | Date | Array<any> | null | undefined;

export interface FilterPersistenceOptions {
  pageId: string;
  autoSave?: boolean;
  storagePrefix?: string;
  permanentFilters?: string[]; // Filter keys that should persist across days
}

export function useFilterPersistence(options: FilterPersistenceOptions) {
  const { pageId, autoSave = true, storagePrefix = 'filters', permanentFilters = [] } = options;

  const getDailyStorageKey = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return `${storagePrefix}_${pageId}_${today}`;
  };

  const getPermanentStorageKey = () => {
    return `${storagePrefix}_${pageId}_permanent`;
  };

  const saveFiltersToStorage = (filters: Record<string, FilterValue>) => {
    try {
      // Separate permanent and daily filters
      const dailyFilters: Record<string, FilterValue> = {};
      const permanentFiltersData: Record<string, FilterValue> = {};
      
      Object.entries(filters).forEach(([key, value]) => {
        if (permanentFilters.includes(key)) {
          permanentFiltersData[key] = value;
        } else {
          dailyFilters[key] = value;
        }
      });

      // Save daily filters
      if (Object.keys(dailyFilters).length > 0) {
        const serializedDailyFilters = Object.entries(dailyFilters).reduce((acc, [key, value]) => {
          if (value instanceof Date) {
            acc[key] = value.toISOString();
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);
        
        localStorage.setItem(getDailyStorageKey(), JSON.stringify(serializedDailyFilters));
      }

      // Save permanent filters
      if (Object.keys(permanentFiltersData).length > 0) {
        const serializedPermanentFilters = Object.entries(permanentFiltersData).reduce((acc, [key, value]) => {
          if (value instanceof Date) {
            acc[key] = value.toISOString();
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);
        
        localStorage.setItem(getPermanentStorageKey(), JSON.stringify(serializedPermanentFilters));
      }
    } catch (error) {
      console.warn('Failed to save filters to localStorage:', error);
    }
  };

  const loadFiltersFromStorage = (): Record<string, any> | null => {
    const dailyStorageKey = getDailyStorageKey();
    const permanentStorageKey = getPermanentStorageKey();
    
    const dailySavedFilters = localStorage.getItem(dailyStorageKey);
    const permanentSavedFilters = localStorage.getItem(permanentStorageKey);
    
    let allFilters: Record<string, any> = {};
    
    // Load daily filters
    if (dailySavedFilters) {
      try {
        const dailyFilters = JSON.parse(dailySavedFilters);
        const deserializedDailyFilters = Object.entries(dailyFilters).reduce((acc, [key, value]) => {
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            acc[key] = new Date(value);
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);
        
        allFilters = { ...allFilters, ...deserializedDailyFilters };
      } catch (error) {
        console.warn('Failed to parse saved daily filters:', error);
      }
    }
    
    // Load permanent filters
    if (permanentSavedFilters) {
      try {
        const permFilters = JSON.parse(permanentSavedFilters);
        const deserializedPermanentFilters = Object.entries(permFilters).reduce((acc, [key, value]) => {
          if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            acc[key] = new Date(value);
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, any>);
        
        allFilters = { ...allFilters, ...deserializedPermanentFilters };
      } catch (error) {
        console.warn('Failed to parse saved permanent filters:', error);
      }
    }
    
    // Only return filters if there are meaningful values (not null, undefined, empty strings, or empty arrays)
    const hasMeaningfulValues = Object.values(allFilters).some(value => {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
      return true;
    });
    
    return hasMeaningfulValues ? allFilters : null;
  };

  const cleanupOldStorageEntries = () => {
    const keysToRemove: string[] = [];
    const today = format(new Date(), 'yyyy-MM-dd');
    
    // Check all localStorage keys for old daily filter entries (all pages)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${storagePrefix}_`) && key.includes('_') && !key.endsWith('_permanent')) {
        // Extract date from key pattern: prefix_pageId_date
        const parts = key.split('_');
        const dateStr = parts[parts.length - 1]; // Last part should be the date
        
        // Validate it's a date format and not today (skip permanent filters)
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/) && dateStr !== today) {
          keysToRemove.push(key);
        }
      }
    }
    
    // Remove old daily entries (permanent filters are preserved)
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  const clearDailyFilters = () => {
    const dailyStorageKey = getDailyStorageKey();
    localStorage.removeItem(dailyStorageKey);
  };

  const clearPermanentFilters = () => {
    const permanentStorageKey = getPermanentStorageKey();
    localStorage.removeItem(permanentStorageKey);
  };

  const restoreFilters = (filterRefs: Record<string, Ref<FilterValue>>) => {
    const savedFilters = loadFiltersFromStorage();
    let filtersRestored = false;
    
    if (savedFilters) {
      Object.entries(filterRefs).forEach(([key, filterRef]) => {
        if (key in savedFilters && savedFilters[key] !== undefined && savedFilters[key] !== null) {
          filterRef.value = savedFilters[key];
          filtersRestored = true;
        }
      });
    }
    
    return filtersRestored;
  };

  const createFilterWatchers = (filterRefs: Record<string, Ref<FilterValue>>) => {
    if (!autoSave) {
      return [];
    }

    const watchers: (() => void)[] = [];
    
    Object.values(filterRefs).forEach(filterRef => {
      const stopWatcher = watch(
        filterRef,
        () => {
          const currentFilters = Object.entries(filterRefs).reduce((acc, [key, ref]) => {
            acc[key] = ref.value;
            return acc;
          }, {} as Record<string, FilterValue>);
          
          saveFiltersToStorage(currentFilters);
        },
        { deep: true }
      );
      
      watchers.push(stopWatcher);
    });
    
    return watchers;
  };

  const manualSave = (filters: Record<string, FilterValue>) => {
    saveFiltersToStorage(filters);
  };

  const clearStoredFilters = () => {
    const dailyStorageKey = getDailyStorageKey();
    const permanentStorageKey = getPermanentStorageKey();
    localStorage.removeItem(dailyStorageKey);
    localStorage.removeItem(permanentStorageKey);
  };

  // Automatically cleanup old entries when composable is initialized
  cleanupOldStorageEntries();

  return {
    saveFiltersToStorage,
    loadFiltersFromStorage,
    restoreFilters,
    createFilterWatchers,
    manualSave,
    clearStoredFilters, // Clears both daily and permanent filters
    clearDailyFilters, // Clears only daily filters for today
    clearPermanentFilters // Clears only permanent filters
  };
} 